import { useRef, useState } from "react";

import { useDroppable } from "@dnd-kit/core";
import {
  faBox,
  faFolder,
  faLink,
  faPen,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { NavLink, type To } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { NO_FOLDER_FILTER } from "src/api";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { FlowFolderListItem } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { DND_NO_FOLDER_ID } from "src/flowsOverview/v2/FlowDndProvider";
import { useCapabilities } from "src/hooks/useCapabilities";
import { buildNavLinkClickHandler } from "src/layout/navLinkClick";
import { useIsContentOverflows } from "src/utils/useIsContentOverflows";

type BaseProps = {
  to: To;
  onRequestSelect: (folderId: string) => void;
  onRequestEdit?: (folder: FlowFolderListItem) => void;
  onRequestDelete: (folder: FlowFolderListItem) => void;
  onCopyLink: (folder: FlowFolderListItem | undefined) => void;
  selected: boolean;
  isScrollAnchor: boolean;
};
export type Props = BaseProps &
  (
    | {
        folder: FlowFolderListItem;
        count?: never;
      }
    | {
        folder: undefined;
        count: number;
      }
  );

export const Folder: React.FC<Props> = ({
  to,
  folder,
  selected,
  isScrollAnchor,
  onRequestSelect,
  onRequestEdit,
  onRequestDelete,
  onCopyLink,
  count,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { flowFolders } = useCapabilities();

  const rawId = folder?.id || DND_NO_FOLDER_ID;
  const droppableId = `sidebar-${rawId}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { folderId: folder?.id ?? null },
  });

  const dropdownItems: OptionsDropdownElement[] = [];
  if (folder !== undefined && flowFolders.canEdit) {
    dropdownItems.push({
      key: "Rename",
      icon: faPen,
      action: () => {
        onRequestEdit?.(folder);
      },
    });
  }

  dropdownItems.push({
    key: "Copy link",
    icon: faLink,
    action: () => {
      onCopyLink(folder);
    },
  });

  if (folder !== undefined && flowFolders.canDelete) {
    dropdownItems.push(MENU_DIVIDER);
    dropdownItems.push({
      key: "Delete",
      icon: faTrashCan,
      disabled:
        folder.flow_count > 0 &&
        "Only empty folders can be deleted. Move or delete the Decision Flows inside it first.",
      action: () => {
        onRequestDelete(folder);
      },
    });
  }

  const folderItemDataLoc =
    (folder ? `folder-${folder?.name}` : "no-folder") +
    (selected ? "-selected" : "");

  const folderItemClassName = twJoin(
    "group/folder mr-2.5 flex h-8 min-w-0 items-center rounded-lg border hover:bg-gray-50",
    selected && "bg-gray-100",
    isOver ? "border-indigo-500" : "border-transparent",
  );

  const folderIcon = (
    <div className="pr-1.5">
      <Icon icon={folder === undefined ? faBox : faFolder} size="xs" />
    </div>
  );

  const folderNameRef = useRef<HTMLSpanElement>(null);

  let content = (
    <div
      className={folderItemClassName}
      data-loc={folderItemDataLoc}
      ref={setNodeRef}
    >
      <NavLink
        className="flex w-full min-w-0 items-center py-1.5 pl-2"
        to={to}
        onClick={(e) => {
          buildNavLinkClickHandler(selected)(e);
          onRequestSelect(folder?.id ?? NO_FOLDER_FILTER);
        }}
      >
        {folderIcon}
        <span
          className="min-w-0 truncate text-left font-inter-medium-13px text-gray-800"
          data-loc="folder-name"
          ref={folderNameRef}
        >
          {folder === undefined ? "No folder" : folder.name}
          &nbsp;
        </span>
        <span
          className="grow text-left font-inter-medium-13px text-gray-500"
          data-loc={
            folder === undefined ? "no-folder-flow-count" : "folder-flow-count"
          }
        >
          ({folder === undefined ? count : folder.flow_count})
        </span>
      </NavLink>
      <EllipsisOptionsDropdown
        buttonClassName="text-gray-800 opacity-0 group-hover/folder:opacity-100 data-[menu-open=true]:opacity-100 pr-2 py-1.5"
        dataLoc="folder-options-dropdown-button"
        elements={dropdownItems}
        iconSize="xs"
        placement="bottom start"
      />
    </div>
  );

  const isOverflowing = useIsContentOverflows(folderNameRef);
  if (folder?.name && isOverflowing) {
    content = (
      <Tooltip
        disabled={!folder?.name}
        open={tooltipOpen}
        onOpenChange={setTooltipOpen}
        placement="right"
        title={folder?.name}
        asChild
      >
        {content}
      </Tooltip>
    );
  }
  return (
    <li className="w-full min-w-0" data-selected={isScrollAnchor || undefined}>
      {content}
    </li>
  );
};
